import {ApolloLink} from '@apollo/client';
import {finalize} from 'rxjs';

let inflightApolloQueryCount = 0;
const apolloNetworkIdleListeners = new Set<() => void>();

/**
 * Event emitter for Apollo network idle state changes.
 *
 * This emitter works in conjunction with `apolloNetworkIdleTrackingLink` to provide
 * a way to listen for when all Apollo GraphQL requests have completed. The tracking
 * link monitors inflight query counts and triggers idle events when the count reaches zero,
 * while this emitter manages the subscription/notification system.
 *
 * Primary use case:
 * - Designer preview mode: Wait for all Apollo queries to finish before initializing
 *   site modules to ensure all dynamic data is loaded before module initialization
 *
 * @example
 * ```typescript
 * const unsubscribe = apolloNetworkIdleEmitter.subscribe(() => {
 *   console.log('All in-flight Apollo requests completed - safe to initialize site modules');
 * });
 *
 * // Later, clean up the subscription
 * unsubscribe();
 * ```
 */
export const apolloNetworkIdleEmitter = {
  subscribe(cb: () => void) {
    apolloNetworkIdleListeners.add(cb);
    return () => {
      apolloNetworkIdleListeners.delete(cb);
    };
  },
};

function emitApolloNetworkIdle() {
  for (const listener of apolloNetworkIdleListeners) {
    listener();
  }
}

/**
 * Apollo Link that tracks network idle state by counting inflight queries.
 * Emits idle events when all Apollo requests complete - useful for testing
 * and waiting for network activity to finish.
 */
export const apolloNetworkIdleTrackingLink = new ApolloLink(
  (operation, forward) => {
    inflightApolloQueryCount++;

    return forward(operation).pipe(
      finalize(() => {
        inflightApolloQueryCount = Math.max(0, inflightApolloQueryCount - 1);

        if (inflightApolloQueryCount === 0) {
          emitApolloNetworkIdle();
        }
      })
    );
  }
);
